import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useLayoutEffect,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  VisibilityState,
  Row,
} from '@tanstack/react-table';

// Hooks
import {
  useIsLandscapeTablet,
  useIsPortraitTablet,
} from '../../../../../../hooks/useMediaBreakPoints';

// Types
import {
  TableProps,
  dataTable,
  SearchbarFilterTypes,
  budgetItemsColumnNames,
} from './types';

// Styled components
import {
  TableContainer,
  ContainerTable,
  FiltersContainer,
  ActiveBudgetItemsTableContainer,
  InactiveBudgetItemsTableContainer,
  TrStyled,
  CategoryBadge,
  PriceCell,
  SwapButtonsContainer,
  SwapBudgetItemButtonDeactivate,
  SwapBudgetItemButtonActivate,
} from './styles';

import {
  Table,
  Tbody,
  Td,
  Th,
  Thead,
} from '../../../summaryTables/SummaryTablesStyles';
import {
  InputRadioContainer,
  InputRadioStyled,
} from '../../../components/MainStyledComponents';
import { Typography } from '../../../../Typography';
import { TruncatedText } from '../../../../TruncatedText';

import SearchBar from '../../../../../inputs/SearchBar';
import { matchesSearch } from '../../../../../../utils/textSearch';

export const BudgetItemsManagementTable = ({
  data,
  onAddActiveBudgetItem,
  onAddInactiveBudgetItem,
}: TableProps): JSX.Element => {
  const { t } = useTranslation('pages');

  // Separar datos activos e inactivos con estado para filtros
  const [activeItemsData, setActiveItemsData] = useState(
    data?.activeItems ?? []
  );
  const [inactiveItemsData, setInactiveItemsData] = useState(
    data?.inactiveItems ?? []
  );

  // Actualizar datos cuando cambian las props
  useEffect(() => {
    setActiveItemsData(data?.activeItems ?? []);
    setInactiveItemsData(data?.inactiveItems ?? []);
  }, [data]);

  // Estados de selección separados
  const [activeBudgetItemsChecked, setActiveBudgetItemsChecked] = useState({});
  const [inactiveBudgetItemsChecked, setInactiveBudgetItemsChecked] = useState(
    {}
  );

  // Responsive hooks
  const isLandscapeTablet = useIsLandscapeTablet();
  const isPortraitTablet = useIsPortraitTablet();

  // Column visibility for responsive
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    isPortraitTablet
      ? {
        usageCount: false,
        unitPrice: false,
        unit: false,
      }
      : isLandscapeTablet
        ? {
          usageCount: false,
        }
        : {}
  );

  useLayoutEffect(() => {
    setColumnVisibility(
      isPortraitTablet
        ? {
          usageCount: false,
          unitPrice: false,
          unit: false,
        }
        : isLandscapeTablet
          ? {
            usageCount: false,
          }
          : {}
    );
  }, [isPortraitTablet, isLandscapeTablet]);

  // Estados de filtros separados
  const [filteredActiveItems, setFilteredActiveItems] = useState(false);
  const [filteredInactiveItems, setFilteredInactiveItems] = useState(false);

  const budgetItemsColumnHelper = createColumnHelper<dataTable>();

  // Columnas para ambas tablas (activos e inactivos)
  const budgetItemsColumns = useMemo(
    () => [
      budgetItemsColumnHelper.display({
        id: 'select',
        cell: ({ row }) => (
          <InputRadioContainer>
            <InputRadioStyled
              name="budgetItemRadio"
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </InputRadioContainer>
        ),
      }),
      budgetItemsColumnHelper.accessor((row) => row.code, {
        id: budgetItemsColumnNames.code.id,
        header: () => t(budgetItemsColumnNames.code.labelKey),
        cell: (info) => info.getValue(),
      }),
      budgetItemsColumnHelper.accessor((row) => row.description, {
        id: budgetItemsColumnNames.description.id,
        header: () => t(budgetItemsColumnNames.description.labelKey),
        cell: (info) => (
          <TruncatedText
            text={info.getValue() || ''}
            maxLength={50}
            size="XS"
          />
        ),
      }),
      budgetItemsColumnHelper.accessor((row) => row.unit, {
        id: budgetItemsColumnNames.unit.id,
        header: () => t(budgetItemsColumnNames.unit.labelKey),
        cell: (info) => info.getValue(),
      }),
      budgetItemsColumnHelper.accessor((row) => row.unitPrice, {
        id: budgetItemsColumnNames.unitPrice.id,
        header: () => t(budgetItemsColumnNames.unitPrice.labelKey),
        cell: (info) => <PriceCell>{info.getValue().toFixed(2)} €</PriceCell>,
      }),
      budgetItemsColumnHelper.accessor((row) => row.budgetCategory, {
        id: budgetItemsColumnNames.category.id,
        header: () => t(budgetItemsColumnNames.category.labelKey),
        cell: (info) => {
          const category = info.getValue();
          const categoryText = `${category.code} - ${category.name}`;
          return (
            <CategoryBadge color={category.color || '#cccccc'}>
              <TruncatedText text={categoryText} maxLength={30} size="XS" />
            </CategoryBadge>
          );
        },
      }),
      budgetItemsColumnHelper.accessor((row) => row.usageCount, {
        id: budgetItemsColumnNames.usageCount.id,
        header: () => t(budgetItemsColumnNames.usageCount.labelKey),
        cell: (info) => info.getValue() || 0,
      }),
    ],
    [activeItemsData, inactiveItemsData]
  );

  // Filtros de búsqueda separados - filtra por código Y descripción
  const activeItemsSearchFilter = useCallback<SearchbarFilterTypes>(
    (value) => {
      if (value === '') {
        setActiveItemsData(data?.activeItems || []);
        setFilteredActiveItems((prev) => !prev);
      } else {
        const filtered = (data?.activeItems || []).filter(
          (item) =>
            matchesSearch(
              [
                item.code,
                item.description,
                item.budgetCategory?.code,
                item.budgetCategory?.name,
              ],
              value
            )
        );
        setActiveItemsData(filtered);
      }
    },
    [data?.activeItems, filteredActiveItems]
  );

  const inactiveItemsSearchFilter = useCallback<SearchbarFilterTypes>(
    (value) => {
      if (value === '') {
        setInactiveItemsData(data?.inactiveItems || []);
        setFilteredInactiveItems((prev) => !prev);
      } else {
        const filtered = (data?.inactiveItems || []).filter(
          (item) =>
            matchesSearch(
              [
                item.code,
                item.description,
                item.budgetCategory?.code,
                item.budgetCategory?.name,
              ],
              value
            )
        );
        setInactiveItemsData(filtered);
      }
    },
    [data?.inactiveItems, filteredInactiveItems]
  );

  // Funciones para manejar clics en filas
  const handleRowClick = (row: any) => {
    row.getToggleSelectedHandler()(row);
  };

  // Funciones de activación/desactivación
  const addActiveBudgetItem = () => {
    const selectedRow =
      activeBudgetItemsTable.getSelectedRowModel().rows[0].original;
    onAddActiveBudgetItem(selectedRow.id);
    activeBudgetItemsTable.resetRowSelection(true);
  };

  const addInactiveBudgetItem = () => {
    const selectedRow =
      inactiveBudgetItemsTable.getSelectedRowModel().rows[0].original;
    onAddInactiveBudgetItem(selectedRow.id);
    inactiveBudgetItemsTable.resetRowSelection(true);
  };

  // Configuración de tablas separadas
  const activeBudgetItemsTable = useReactTable({
    getRowId: (row: any) => row.id,
    data: activeItemsData,
    columns: budgetItemsColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setActiveBudgetItemsChecked,
    state: {
      rowSelection: activeBudgetItemsChecked,
      columnVisibility,
    },
    enableRowSelection: true,
    enableMultiRowSelection: false,
    onColumnVisibilityChange: setColumnVisibility,
  });

  const inactiveBudgetItemsTable = useReactTable({
    getRowId: (row: any) => row.id,
    data: inactiveItemsData,
    columns: budgetItemsColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setInactiveBudgetItemsChecked,
    state: {
      rowSelection: inactiveBudgetItemsChecked,
      columnVisibility,
    },
    enableRowSelection: true,
    enableMultiRowSelection: false,
    onColumnVisibilityChange: setColumnVisibility,
  });

  return (
    <ContainerTable>
      {/* TABLA DE PARTIDAS ACTIVAS */}
      <ActiveBudgetItemsTableContainer>
        <Typography size="L" variant="semiBold">
          {t('management.activeBudgetItems')} ({activeItemsData.length})
        </Typography>
        <FiltersContainer>
          <SearchBar
            variant="form"
            onChangeInput={(e) => activeItemsSearchFilter(e.target.value)}
          />
        </FiltersContainer>
        <TableContainer>
          <Table>
            <Thead>
              {activeBudgetItemsTable.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </Th>
                  ))}
                </tr>
              ))}
            </Thead>
            <Tbody>
              {activeBudgetItemsTable.getRowModel().rows.map((row) => (
                <TrStyled
                  key={row.id}
                  checked={row.getIsSelected()}
                  indeterminate={
                    !activeBudgetItemsTable.getIsSomeRowsSelected()
                  }
                  onClick={() => {
                    handleRowClick(row);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </TrStyled>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </ActiveBudgetItemsTableContainer>

      {/* BOTONES DE INTERCAMBIO CENTRADOS */}
      <SwapButtonsContainer>
        <SwapBudgetItemButtonDeactivate
          onClick={addActiveBudgetItem}
          disabled={
            activeBudgetItemsTable.getSelectedRowModel().rows.length === 0
          }
        >
          ⬇ Desactivar
        </SwapBudgetItemButtonDeactivate>
        <SwapBudgetItemButtonActivate
          onClick={addInactiveBudgetItem}
          disabled={
            inactiveBudgetItemsTable.getSelectedRowModel().rows.length === 0
          }
        >
          ⬆ Activar
        </SwapBudgetItemButtonActivate>
      </SwapButtonsContainer>

      {/* TABLA DE PARTIDAS INACTIVAS */}
      <InactiveBudgetItemsTableContainer>
        <Typography size="L" variant="semiBold">
          {t('management.inactiveBudgetItems')} ({inactiveItemsData.length})
        </Typography>
        <FiltersContainer>
          <SearchBar
            variant="form"
            onChangeInput={(e) => inactiveItemsSearchFilter(e.target.value)}
          />
        </FiltersContainer>
        <TableContainer>
          <Table>
            <Thead>
              {inactiveBudgetItemsTable.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </Th>
                  ))}
                </tr>
              ))}
            </Thead>
            <Tbody>
              {inactiveBudgetItemsTable.getRowModel().rows.map((row) => (
                <TrStyled
                  key={row.id}
                  checked={row.getIsSelected()}
                  indeterminate={
                    !inactiveBudgetItemsTable.getIsSomeRowsSelected()
                  }
                  onClick={() => {
                    handleRowClick(row);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </TrStyled>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </InactiveBudgetItemsTableContainer>
    </ContainerTable>
  );
};
